import { useEffect, useMemo } from "react";
import { useParams } from "wouter";
import { useAssets } from "@/contexts/AssetContext.jsx";
import { useEnalysisOptimizationContext } from "@/contexts/EnalysisOptimizationContext.jsx";
import AssetLayout from "./asset-layout.jsx";
import { getEnalysisVitalSignsViewUrl } from "@/api/enalysis-api.js";
import { normalizeVitalSignsResponse } from "@/lib/vitalsigns-utils.js";
import OptimizationDashboard from "@/components/optimization/optimization-dashboard.jsx";
import OptimizationEmptyState from "@/components/optimization/optimization-empty-state.jsx";

/**
 * Asset Optimization page: route + Enalysis cache wiring; dashboard vs empty state.
 * @returns {JSX.Element}
 */
export default function AssetOptimization() {
  const { id } = useParams();
  const { assets: compressors } = useAssets();
  const compressorId = decodeURIComponent(id);
  const compressor = compressors?.find(
    (c) =>
      c.assetNumber === compressorId
      || c.assetNumber === id
      || c.id === compressorId
      || c.id === id
  );
  // Missing compressor: AssetLayout shows loading or "Asset Not Found". Do not return null here.

  const { getOptimizationData, fetchOptimizationData, dataRefreshToken } =
    useEnalysisOptimizationContext();

  const hasEnalysisIds = compressor?.optimizationEnabled === true;
  const compNum = compressor?.enalysisCompressorNum;
  const compId = compressor?.enalysisCompanyId;
  const {
    vitalSigns,
    report,
    fieldData,
    modules,
    frames,
    vitalSignsLoading,
    vitalSignsError,
    operatingConditionsLoading,
    equipmentInfoLoading,
  } = getOptimizationData(compNum, compId);

  const isUnauthorizedError =
    !!vitalSignsError && /401|unauthorized/i.test(vitalSignsError);

  const isWrongCompanyOrAssetError =
    !!vitalSignsError && /404|400|not found|bad request/i.test(vitalSignsError);

  useEffect(() => {
    if (!hasEnalysisIds || compNum == null || compId == null) return;
    fetchOptimizationData(compNum, compId);
  }, [
    hasEnalysisIds,
    compNum,
    compId,
    fetchOptimizationData,
    dataRefreshToken,
  ]);

  const normalized = useMemo(
    () =>
      vitalSigns
        ? normalizeVitalSignsResponse(
            vitalSigns,
            report,
            fieldData,
            modules,
            frames
          )
        : null,
    [vitalSigns, report, fieldData, modules, frames]
  );

  const showDashboard =
    !!normalized
    && ((normalized.vitalSigns?.length ?? 0) > 0
      || (normalized.recommendedActions?.length ?? 0) > 0
      || Object.keys(normalized.operatingConditions).length > 0
      || Object.keys(normalized.equipmentInfo).length > 0
      || (normalized.flagDiagnostics
        && Object.keys(normalized.flagDiagnostics).length > 0));

  const enalysisViewUrl =
    hasEnalysisIds && compNum != null && compId != null
      ? getEnalysisVitalSignsViewUrl(compNum, compId)
      : null;

  return (
    <AssetLayout>
      {compressor ? (
        <div className='space-y-6'>
          {showDashboard && normalized ? (
            <OptimizationDashboard
              normalized={normalized}
              enalysisViewUrl={enalysisViewUrl}
              operatingConditionsLoading={operatingConditionsLoading}
              equipmentInfoLoading={equipmentInfoLoading}
              compNum={compNum}
              compId={compId}
            />
          ) : (
            <OptimizationEmptyState
              hasEnalysisIds={hasEnalysisIds}
              isWrongCompanyOrAssetError={isWrongCompanyOrAssetError}
              vitalSignsLoading={vitalSignsLoading}
              vitalSignsError={vitalSignsError}
              isUnauthorizedError={isUnauthorizedError}
            />
          )}
        </div>
      ) : null}
    </AssetLayout>
  );
}
